import React, {useEffect, useState, useRef, useCallback} from 'react';
import {Grid, Typography} from "@mui/material";
import moment from "moment";
import {DateFormats} from "../../components/constants/securspace-constants";
import {withSnackbar} from "../../components/hocs/withSnackbar";
import {getErrorMessageForNonStandardAndStandardResponse} from "../../util/NetworkErrorUtil";
import Pagination from "../../components/deprecatedUikit/Pagination/Pagination";
import GroupSummaries from '../../components/GroupSummaries/GroupSummaries/GroupSummaries';
import GroupSummariesLoader from '../../components/GroupSummaries/GroupSummaries/GroupSummariesLoader';
import { verbiage } from "../../components/payouts/payouts-report-verbiage";
import { groupSummaryFields } from "../../components/payouts/data";
import {requestPayoutsPaginated, requestCalculatePayoutsSummary} from "../../components/payouts/request/payouts-requests";
import {formatCurrencyValue} from "../../util/PaymentUtils";
import SecurSpaceContainer from "../../components/common/SecurSpaceContainer";
import PayoutsFilter from "../../components/payouts/PayoutsFilter";
import PayoutsTable from "../../components/payouts/PayoutsTable";
import PayoutsExportCSVs from "../../components/payouts/PayoutsExportCSVs";
import NoMatchingInvoicesFound from '../../components/NoMatchingInvoicesFound';
import InfoCardLoadingSkeleton from "../../components/skeleton/InfoCardLoadingSkeleton";
import useScrollToTop from "../../hooks/scroll/useScrollToTop";
import {SecurSpaceReportContainer, SecurSpaceReportTitle} from "../../components/common";

const initialFilterParams = {
  locationId: '',
  locationName: 'All',
  status: '',
  payoutCompleted: '',
  startDate: moment().add(-1, 'years').format(DateFormats.DAY).toString(),
  endDate: moment().format(DateFormats.DAY).toString(),
  sortBy: 'CREATED_ON',
  sortDir: 'asc',
};

const SupplierPayoutsReport = ({
  snackbarShowMessage
}) => {

  const [loading, setLoading] = useState(false)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [query, setQuery] = useState({
    locationName: '',
    page: 0,
    pageSize: 10,
    ...initialFilterParams
  });
  const [payoutsItems, setPayoutsItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const needsNewSummary = useRef(true);

  const handlePageChange = useCallback((_, newPage) => {
    setPage(newPage)
  }, []);

  const handleRowsPerPageChange = (event) => {
    const pageSize = parseInt(event.target.value, 10);
    setRowsPerPage(pageSize);
  };

  const handleReviewItem = (item) => {
    item && (window.open(`/finances/invoices-in-payout/${item.payoutId}`, '_blank'));
  };

  const handleSortChange = (sortBy, order) => {
    setQuery(prev => ({...prev, sortBy: sortBy, sortDir: order}))
  };

  useEffect(() => {
    setLoading(true);

    const request = {
      ...query,
      pageSize: rowsPerPage,
      page: page
    }

    requestPayoutsPaginated(request)
    .then((response) => {
      if (response.body) {
        const {content, totalPages, count, page} = response.body;
        setTotalItems(count);
        setPayoutsItems(content.map((item) => {
          item.payoutAmount = formatCurrencyValue(item.payoutAmount);
          return item;
        }));
        if (page > 0 && page + 1 > totalPages) {
          // If the page index is greater than the total pages
          // This will refresh the data and show the last page of results
          handlePageChange(null, totalPages > 0 ? totalPages - 1 : 0);
        } else {
          setLoading(false);
        }
      }
    })
    .catch((error) => {
      snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error), 'error');
      setLoading(false);
    })

    if (needsNewSummary.current) {
      needsNewSummary.current = false;
      setSummaryLoading(true);
      requestCalculatePayoutsSummary(query).then(({body}) => {
        if (body) {
          groupSummaryFields[0].value = formatCurrencyValue(body.totalPayouts, true);
          groupSummaryFields[1].value = body.payoutsCount;
          groupSummaryFields[2].value = body.transactionCount;
        }
      }).catch(err => {
        snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(err), "error", 15000)
      })
      .finally(() => setSummaryLoading(false));
    }

  }, [handlePageChange, query, snackbarShowMessage, page, rowsPerPage]);

  useScrollToTop([loading]);

  return (
    <SecurSpaceContainer>
      <SecurSpaceReportContainer spacing={2}>
          <SecurSpaceReportTitle marginBottom={"2em"}>
            {verbiage.title}
          </SecurSpaceReportTitle>
          <PayoutsFilter
            setQuery={setQuery}
            defaultFilterParams={initialFilterParams}
            page={page}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
            needsNewSummary={needsNewSummary}
          />

          <Grid item container justifyContent={"space-between"} mb={6} gap={2}>
            <Grid item lg={6}>
              <Typography
                variant='body2'
                sx={{ textTransform: 'uppercase', color: '#000'}}
                component='p'
              >
                {query.locationName} (SHOWING {payoutsItems.length} RECORDS IN GROUP)
              </Typography>
            </Grid>
            <PayoutsExportCSVs
              item
              query={query}
              disabled={loading || summaryLoading || !payoutsItems.length}
            />
          </Grid>

          <Grid item xs={12}>
            {
              summaryLoading ? <GroupSummariesLoader />
                : <GroupSummaries groupSummaryFields={groupSummaryFields} styles={{visibility: payoutsItems.length ? 'visible' : 'hidden'}}/>
            }
          </Grid>
          <Grid item container xs={12}>
            {
              loading ? <Grid item container xs={12} direction={'column'}> <InfoCardLoadingSkeleton cards={rowsPerPage} /> </Grid>
                : payoutsItems.length ? <PayoutsTable
                  items={payoutsItems}
                  handleReviewItem={handleReviewItem}
                  handleSortChange={handleSortChange}
                  sortBy={query.sortBy}
                  sortDir={query.sortDir}
                />
                  : <Grid item container justifyContent={'center'}> <NoMatchingInvoicesFound message="No payouts found" /> </Grid>
            }
            <Grid item container justifyContent={'end'} alignItems={'end'} sx={{ mt:4 }}>
              <Pagination
                count={totalItems}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                variant="tablePagination"
              />
            </Grid>
          </Grid>
      </SecurSpaceReportContainer>
    </SecurSpaceContainer>
  );
};

export default withSnackbar(SupplierPayoutsReport);
